/**
 * Tabela wierszy z GET /kontrola-faktur/lista.
 *
 * Kolumny (kontrakt): Status, Numer, Typ, Wystawiona, Wiek, Brutto (portal),
 * W systemie (sklep · dodał · kiedy · kwota; wiersz na kazdy wpis, link do
 * /purchase-invoices/<faktura_id>), Termin platnosci.
 * Kontener ma overflow: auto - tabela nie rozpycha strony.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  STATUSY, TYPY, NIEZNANY_ADRES, KOLOR_PARAGON, TLO_PARAGON, TOOLTIP_PARAGON_BEZ_XML, TOOLTIP_OSOZ_BEZ_PDF,
  etykietaZrodlaPliku, formatujDate, formatujDateCzas, formatujKwote,
  TOOLTIP_FA3, FORMATY_BEZ_KREATORA, maOdznakeKsef, STATUSY_KSEF,
} from './format';
import DodajOsozModal from './DodajOsozModal';
import { kontrolaFakturService } from '../../services/kontrolaFakturService';
import toast from 'react-hot-toast';

const th = (tekst, extra = {}) => (
  <th key={tekst} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', ...extra }}>{tekst}</th>
);
const td = { padding: '5px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', fontSize: '11px' };

export const ZnacznikStatusu = ({ status }) => {
  const s = STATUSY[status] || { etykieta: status || '—', kolor: 'var(--text-secondary)', tlo: 'var(--bg)' };
  return (
    <span data-testid="status" style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', color: s.kolor, backgroundColor: s.tlo }}>
      {s.etykieta}
    </span>
  );
};

/** Wiek: "świeża" (<= 2 dni), "zaległa" (> 7 dni) - flagi liczy backend. */
export const ZnacznikWieku = ({ wiek_dni, swieza, zalegla }) => {
  const dni = wiek_dni === null || wiek_dni === undefined ? '—' : `${wiek_dni} dn.`;
  let etykieta = null;
  let kolor = 'var(--text-muted)';
  let tlo = 'transparent';
  if (swieza) { etykieta = 'świeża'; kolor = 'var(--success)'; tlo = 'var(--success-light)'; }
  else if (zalegla) { etykieta = 'zaległa'; kolor = 'var(--danger)'; tlo = 'var(--danger-light)'; }
  return (
    <span data-testid="wiek" style={{ whiteSpace: 'nowrap' }}>
      <span style={{ color: 'var(--text)' }}>{dni}</span>
      {etykieta && (
        <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: 600, color: kolor, backgroundColor: tlo }}>{etykieta}</span>
      )}
    </span>
  );
};

/** Skąd sklep (sklep_wg_sfd.zrodlo): faktury z adresu dostawy (mapa/nauka), paragony Renomy z maila
 *  (ANEKS 6f: temat > treść > odpowiedź w wątku) albo z dokumentu już w systemie. */
const ZRODLA_SKLEPU = {
  mapa: 'mapowanie ręczne', nauka: 'nauka z faktur', system: 'dokument już w systemie',
  temat: 'temat maila', tresc: 'treść maila', watek: 'odpowiedź w wątku maila',
};

/** Kolumna "Sklep wg SFD" (ANEKS 1): nazwa sklepu z adresu dostawy; brak = "nieznany adres".
 *  Paragon ze sklepem z wątku maila (ANEKS 6f) ma odznakę „z wątku” i fragment maila w tooltipie;
 *  konflikt (dwa sklepy w jednej wiadomości) = "nieznany adres" + "konflikt Kalisz/Szperk". */
export const SklepWgSfd = ({ sklep, adres, wz, konflikt }) => {
  const konfliktSklepow = Array.isArray(konflikt) && konflikt.length > 0 ? konflikt : null;
  const podtytul = [adres, wz ? `WZ: ${wz}` : null].filter(Boolean).join(' · ');
  const wskazowka = sklep?.wskazowka;
  const tooltip = [
    adres && !konfliktSklepow ? `Adres dostawy: ${adres}` : null,
    wz ? `WZ: ${wz}` : null,
    sklep?.zrodlo ? `źródło: ${ZRODLA_SKLEPU[sklep.zrodlo] || sklep.zrodlo}` : null,
    wskazowka?.fragment ? `„${wskazowka.fragment}”${wskazowka.data ? ` (${formatujDateCzas(wskazowka.data)})` : ''}` : null,
    konfliktSklepow ? `W jednej wiadomości wątku maila wymieniono kilka sklepów: ${konfliktSklepow.join(', ')} — wskaż sklep ręcznie (mapa słów w ustawieniach SFD B2B)` : null,
  ].filter(Boolean).join('\n');
  if (!sklep && !adres && !konfliktSklepow) return <span data-testid="sklep-sfd" style={{ color: 'var(--text-muted)' }}>—</span>;
  return (
    <span data-testid="sklep-sfd" title={tooltip || undefined} style={{ display: 'inline-block', lineHeight: 1.25 }}>
      {sklep?.nazwa
        ? (
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {sklep.nazwa}
            {sklep.zrodlo === 'watek' && (
              <span data-testid="sklep-z-watku" style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600, color: 'var(--info)', backgroundColor: 'var(--info-light)' }}>z wątku</span>
            )}
          </span>
        )
        : <span style={{ fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--warning)' }}>{NIEZNANY_ADRES}</span>}
      {konfliktSklepow
        ? <span data-testid="sklep-konflikt" style={{ display: 'block', fontSize: '10px', color: 'var(--warning)', whiteSpace: 'nowrap' }}>konflikt {konfliktSklepow.join('/')}</span>
        : podtytul && <span style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{podtytul}</span>}
    </span>
  );
};

/** Wpisy w systemie; wpis znaleziony pod numerem WZ dostaje dopisek "przez WZ-…" (ANEKS 2).
 *  Korekta dodana w kontroli korekt SFD ma w systemie WŁASNY numer KFZ (numer FSK z portalu siedzi
 *  w nr_zamowienia) – pokazujemy go obok odnośnika, żeby było widać, który dokument to jest. */
const WSystemie = ({ wpisy, status, przezWz, numer }) => {
  const listaWz = Array.isArray(przezWz) ? przezWz.filter(Boolean) : [];
  if (!wpisy || wpisy.length === 0) {
    return (
      <span style={{ color: status === 'brak' ? 'var(--danger)' : 'var(--text-muted)' }}>
        —{listaWz.length > 0 && <span style={{ color: 'var(--info)', marginLeft: '6px' }}>przez {listaWz.join(', ')}</span>}
      </span>
    );
  }
  // WZ z wiersza, ktorych nie ma przy zadnym wpisie - pokaz zbiorczo pod spodem
  const naWpisach = new Set(wpisy.map(w => w.przez_wz).filter(Boolean));
  const luzem = listaWz.filter(wz => !naWpisach.has(wz));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {wpisy.map(w => (
        <div key={w.faktura_id} style={{ whiteSpace: 'nowrap' }}>
          <Link
            to={`/purchase-invoices/${w.faktura_id}`}
            data-testid="link-faktura"
            title={`Otwórz fakturę #${w.faktura_id}${w.status ? ` (${w.status})` : ''}`}
            style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}
          >
            {w.sklep || `#${w.faktura_id}`}
          </Link>
          {w.numer && w.numer !== numer && !w.przez_wz && (
            <span data-testid="numer-w-systemie" style={{ color: 'var(--text-muted)', marginLeft: '6px' }}>{w.numer}</span>
          )}
          <span style={{ color: 'var(--text-muted)' }}> · {w.dodal || '—'} · {formatujDateCzas(w.data_dodania)} · </span>
          <span style={{ fontWeight: 600 }}>{formatujKwote(w.suma_brutto)}</span>
          {w.przez_wz && <span data-testid="przez-wz" style={{ color: 'var(--info)', marginLeft: '6px' }}>przez {w.przez_wz}</span>}
        </div>
      ))}
      {luzem.length > 0 && <div data-testid="przez-wz" style={{ color: 'var(--info)', whiteSpace: 'nowrap' }}>przez {luzem.join(', ')}</div>}
    </div>
  );
};

const btnMaly = (tlo, kolor, ramka) => ({
  padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap',
  border: `1px solid ${ramka}`, background: tlo, color: kolor,
});

/** Statusy wiersza, przy ktorych ma sens sciaganie pliku z portalu (faktura nie jest jeszcze w systemie). */
const DO_DODANIA = new Set(['brak', 'czesciowo_wz', 'dodana_jako_wz']);

/** Odznaka "KSeF" przy numerze (hook KSeF): wiersz kontroli powiazany z faktura z ksef_faktury_otrzymane
 *  (`zrodla` zawiera 'ksef'); tooltip z pelnym numerem KSeF i statusem wiersza KSeF. */
export const OdznakaKsef = ({ ksef }) => {
  const status = ksef?.status ? (STATUSY_KSEF[ksef.status] || ksef.status) : null;
  const tytul = [ksef?.ksef_numer ? `KSeF: ${ksef.ksef_numer}` : 'Faktura jest w KSeF', status ? `status w zakładce „Faktury z KSeF”: ${status}` : null]
    .filter(Boolean).join('\n');
  return (
    <span data-testid="odznaka-ksef" title={tytul}
      style={{ display: 'inline-block', marginLeft: '6px', padding: '0 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--info)', backgroundColor: 'var(--info-light)', verticalAlign: 'middle' }}>
      KSeF
    </span>
  );
};

/** Odznaka typu 'paragon' w kolumnie Typ (ANEKS 6). */
export const OdznakaParagonu = () => (
  <span data-testid="typ-paragon" title="Paragon z NIP od SFD S02 Wrocław Renoma"
    style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', color: KOLOR_PARAGON, backgroundColor: TLO_PARAGON }}>
    {TYPY.paragon}
  </span>
);

/**
 * Maly znacznik przy pliku: skad go mamy i czy jest bez cennika (ANEKS 4);
 * paragony: 'z maila (XML)' / 'z listy Renomy (bez XML)', OSOZ: 'XML OSOZ z maila' (ANEKS 6/6b).
 */
export const ZnacznikPliku = ({ plik, wiersz }) => {
  const etykieta = etykietaZrodlaPliku(plik, wiersz);
  if (!etykieta) return null;
  const p = plik || {};
  const zMaila = p.zrodlo === 'mail';
  const bezPliku = etykieta === 'z listy Renomy (bez XML)';
  const osoz = p.format === 'osoz';
  const fa3 = p.format === 'fa3';
  const tytul = [
    p.nazwa ? `Plik: ${p.nazwa}` : null,
    zMaila && p.mail_temat ? `Temat maila: ${p.mail_temat}` : null,
    zMaila && p.mail_data ? `Data maila: ${formatujDateCzas(p.mail_data)}` : null,
    osoz ? `XML OSOZ-EDI z maila${p.pdf ? ' + PDF z kodami EAN' : ''} — dodawanie bez kreatora, produkty muszą już być w systemie` : null,
    osoz && !p.pdf ? TOOLTIP_OSOZ_BEZ_PDF : null,
    fa3 ? TOOLTIP_FA3 : null,
    wiersz?.typ === 'paragon' && p.pdf ? 'PDF: wydruk paragonu z maila (przycisk „PDF” otwiera podgląd)' : null,
    bezPliku ? TOOLTIP_PARAGON_BEZ_XML : null,
    p.bez_cennika && !osoz && wiersz?.typ !== 'paragon' ? 'W paczce jest sam dokument — bez cennika' : null,
  ].filter(Boolean).join('\n');
  const tylkoXml = osoz && !p.pdf;
  const kolor = bezPliku ? 'var(--text-muted)' : tylkoXml ? 'var(--warning)' : zMaila ? 'var(--purple)' : 'var(--info)';
  const tlo = bezPliku ? 'var(--bg)' : tylkoXml ? 'var(--warning-light)' : zMaila ? 'var(--purple-light)' : 'var(--info-light)';
  // dopisek "bez cennika" tylko tam, gdzie kreator ma krok 1 do pominiecia (nie paragon, nie OSOZ, nie KSeF)
  const pokazBezCennika = Boolean(p.bez_cennika) && !osoz && !fa3 && wiersz?.typ !== 'paragon';
  return (
    <span data-testid="znacznik-pliku" title={tytul || undefined} style={{ display: 'inline-block', lineHeight: 1.25 }}>
      <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '8px', whiteSpace: 'nowrap', color: kolor, backgroundColor: tlo }}>
        {etykieta}
      </span>
      {pokazBezCennika && (
        <span data-testid="bez-cennika" style={{ display: 'block', fontSize: '10px', color: 'var(--warning)', whiteSpace: 'nowrap' }}>
          bez cennika (krok 1 pominięty)
        </span>
      )}
    </span>
  );
};

/** ANEKS 6e: podglad PDF z maila (wydruk paragonu) w nowej karcie - GET plik/pdf jako blob (z sesja). */
export const otworzPdf = async (wiersz) => {
  try {
    const blob = await kontrolaFakturService.pobierzPdf(wiersz.id);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
  } catch (e) {
    toast.error(e?.data?.message || e?.message || 'Nie udało się otworzyć PDF.');
  }
};

/** Przycisk "PDF" przy paragonie z wydrukiem z maila (plik.pdf) - otwiera podglad w nowej karcie;
 * przy fakturze (2026-09-13) - wydruk z portalu SFD zapisany obok wiersza. */
export const PrzyciskPdf = ({ wiersz, onPdf }) => (
  <button type="button" data-testid="btn-pdf" onClick={() => (onPdf || otworzPdf)(wiersz)}
    title={wiersz.typ === 'paragon'
      ? `Podgląd wydruku paragonu ${wiersz.numer || ''} (PDF z maila) — otwiera się w nowej karcie`
      : `Podgląd PDF faktury ${wiersz.numer || ''} (wydruk z portalu SFD) — otwiera się w nowej karcie`}
    style={btnMaly('var(--bg)', 'var(--text-primary)', 'var(--border)')}>
    PDF
  </button>
);

/**
 * Faktura bez pliku (plik.status brak / brak_w_portalu): "PDF z portalu" -> POST /<id>/pdf-z-portalu (wydruk
 * do podgladu obok wiersza, bez importu z samego PDF); gdy wydruk juz jest (plik.pdf) - przycisk "PDF" (podglad).
 */
const PdfZPortalu = ({ wiersz, onPdfZPortalu, pobieranie, onPdf }) => {
  if (wiersz.typ !== 'faktura' || !onPdfZPortalu) return null;
  if (wiersz.plik?.pdf) return <PrzyciskPdf wiersz={wiersz} onPdf={onPdf} />;
  return (
    <button type="button" data-testid="btn-pdf-z-portalu" disabled={pobieranie} onClick={() => onPdfZPortalu(wiersz)}
      title="Pobierze wydruk PDF tej faktury z portalu SFD (podgląd; import nadal z ZIP-a albo XML+PDF z maila)"
      style={{ ...btnMaly('var(--card-bg)', 'var(--text-secondary)', 'var(--border)'), opacity: pobieranie ? 0.6 : 1 }}>
      {pobieranie ? 'Pobieram PDF…' : 'PDF z portalu'}
    </button>
  );
};

/**
 * Kolumna "Akcje" (ANEKS 3): "Dodaj" (mozna_dodac) -> kreator z ?sfd=<id>;
 * "Pobierz plik" (plik brak/znaleziony/blad) -> POST plik/pobierz; 'brak_w_portalu' -> szary tekst.
 * ANEKS 6e: paragon z PDF-em z maila (plik.pdf) ma zawsze przycisk "PDF" (takze gdy juz w systemie).
 */
export const AkcjeWiersza = ({ wiersz, onDodaj, onDodajOsoz, onPobierzPlik, pobieranie, onPdf, onPdfZPortalu, pobieraniePdf }) => {
  const plik = wiersz.plik;
  const doDodania = DO_DODANIA.has(wiersz.status) && wiersz.typ !== 'wz';
  const pdfParagonu = wiersz.typ === 'paragon' && plik?.pdf ? <PrzyciskPdf wiersz={wiersz} onPdf={onPdf} /> : null;
  // ANEKS 6b/6d: XML OSOZ (+PDF z EAN) z maila - zapis bez kreatora (modal), nie ma cennika ani ZIP-a.
  // KSeF: XML FA(3) (plik.format 'fa3') - ta sama sciezka (/dodaj-osoz rozpoznaje format), przycisk "Dodaj z KSeF".
  if (wiersz.mozna_dodac && FORMATY_BEZ_KREATORA.has(plik?.format)) {
    const fa3 = plik.format === 'fa3';
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <button type="button" data-testid={fa3 ? 'btn-dodaj-ksef' : 'btn-dodaj-osoz'} onClick={() => onDodajOsoz && onDodajOsoz(wiersz)}
          title={fa3
            ? 'Portal ani poczta nie dały pliku — zapis z XML FA(3) z KSeF (GTIN = EAN; produkty muszą już być w systemie)'
            : `Portal nie dał paczki ZIP — zapis z XML${plik.pdf ? ' + PDF (EAN)' : ''} z maila (produkty muszą już być w systemie)`}
          style={fa3 ? btnMaly('var(--info)', '#fff', 'var(--info)') : btnMaly('var(--purple)', '#fff', 'var(--purple)')}>
          {fa3 ? 'Dodaj z KSeF' : 'Dodaj z maila'}
        </button>
        <ZnacznikPliku plik={plik} wiersz={wiersz} />
      </span>
    );
  }
  if (wiersz.mozna_dodac) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <button type="button" data-testid="btn-dodaj" onClick={() => onDodaj && onDodaj(wiersz)}
          title={plik?.nazwa ? `Plik z SFD: ${plik.nazwa}` : 'Otwórz kreator z plikami z SFD'}
          style={btnMaly('var(--primary)', '#fff', 'var(--primary)')}>
          Dodaj
        </button>
        {pdfParagonu}
        <ZnacznikPliku plik={plik} wiersz={wiersz} />
      </span>
    );
  }
  // ANEKS 6: paragon bez XML - nie ma go w portalu, wiec nie "Pobierz plik"; czekamy na maila z Renomy
  if (wiersz.typ === 'paragon') {
    if (!doDodania) {
      // np. paragon juz w systemie - zostaje sam podglad PDF (ANEKS 6e), inaczej kreska
      return pdfParagonu || <span style={{ color: 'var(--text-muted)' }}>—</span>;
    }
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <button type="button" data-testid="btn-dodaj-paragon-bez-xml" disabled title={TOOLTIP_PARAGON_BEZ_XML}
          style={{ ...btnMaly('var(--bg)', 'var(--text-muted)', 'var(--border)'), cursor: 'not-allowed' }}>
          Dodaj
        </button>
        {pdfParagonu}
        <ZnacznikPliku plik={plik} wiersz={wiersz} />
      </span>
    );
  }
  if (!plik || !doDodania) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const pdfZPortalu = ['brak', 'brak_w_portalu'].includes(plik.status)
    ? <PdfZPortalu wiersz={wiersz} onPdfZPortalu={onPdfZPortalu} pobieranie={pobieraniePdf} onPdf={onPdf} />
    : null;
  if (plik.status === 'brak_w_portalu') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <span data-testid="brak-pliku" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>brak pliku w SFD</span>
        {pdfZPortalu}
      </span>
    );
  }
  if (plik.status === 'pobrany') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }} title={plik.nazwa || ''}>plik pobrany</span>
        <ZnacznikPliku plik={plik} wiersz={wiersz} />
      </span>
    );
  }
  // 'brak' | 'znaleziony' | 'blad' -> pobierz (przy bledzie: ponow, tooltip z bledem)
  return (
    <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      {plik.status === 'blad' && (
        <span data-testid="blad-pliku" title={plik.blad || 'błąd pliku'} style={{ color: 'var(--danger)', cursor: 'help' }}>błąd pliku</span>
      )}
      <button type="button" data-testid="btn-pobierz-plik" disabled={pobieranie}
        onClick={() => onPobierzPlik && onPobierzPlik(wiersz)}
        title={plik.status === 'znaleziony' ? `Plik ${plik.nazwa || ''} jest w portalu — pobierz teraz` : 'Poszukaj i pobierz plik ZIP z portalu SFD'}
        style={{ ...btnMaly('var(--card-bg)', 'var(--primary)', 'var(--primary)'), opacity: pobieranie ? 0.6 : 1 }}>
        {pobieranie ? 'Pobieram…' : 'Pobierz plik'}
      </button>
      {pdfZPortalu}
    </span>
  );
};

/**
 * Props (ANEKS 6b): `sklepy` [{id, nazwa}] do selectu w modalu OSOZ; `onDodano(wynik, wiersz)` po udanym
 * zapisie z XML (strona odswieza liste/podsumowanie). Modal jest tu, bo tabela zna wiersz.
 * `onPdfZPortalu(wiersz)` + `pobieraniePdfId` (2026-09-13): "PDF z portalu" przy fakturze bez pliku.
 */
const TabelaKontroli = ({ wiersze, ladowanie, onDodaj, onPobierzPlik, pobieranieId, sklepy, onDodano, onPdf, onPdfZPortalu, pobieraniePdfId }) => {
  const lista = Array.isArray(wiersze) ? wiersze : [];
  const navigate = useNavigate();
  const dodaj = onDodaj || ((w) => navigate(`/purchase-invoices/kreator?sfd=${w.id}`));
  const [osozWiersz, setOsozWiersz] = useState(null);
  return (
    <div data-testid="tabela-kontroli" style={{ overflow: 'auto', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)' }}>
      {osozWiersz && (
        <DodajOsozModal
          wiersz={osozWiersz}
          sklepy={sklepy}
          onZamknij={() => setOsozWiersz(null)}
          onDodano={(wynik, w) => { if (onDodano) onDodano(wynik, w); }}
        />
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)', zIndex: 1 }}>
          <tr>
            {th('Status')}
            {th('Numer')}
            {th('Typ')}
            {th('Sklep wg SFD')}
            {th('Wystawiona')}
            {th('Wiek')}
            {th('Brutto (portal)', { textAlign: 'right' })}
            {th('W systemie')}
            {th('Termin płatności')}
            {th('Akcje')}
          </tr>
        </thead>
        <tbody>
          {lista.map((w, idx) => (
            <tr key={`${w.zrodlo || 'sys'}-${w.numer}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
              <td style={td}><ZnacznikStatusu status={w.status} /></td>
              <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text)' }}>
                {w.numer}
                {maOdznakeKsef(w) && <OdznakaKsef ksef={w.ksef} />}
              </td>
              <td style={{ ...td, fontWeight: w.typ === 'wz' ? 600 : 400, color: w.typ === 'korekta' ? 'var(--orange)' : w.typ === 'wz' ? 'var(--info)' : 'var(--text-secondary)' }}>
                {w.typ === 'paragon' ? <OdznakaParagonu /> : (TYPY[w.typ] || w.typ || '—')}
              </td>
              <td style={td}><SklepWgSfd sklep={w.sklep_wg_sfd} adres={w.adres_dostawy} wz={w.powiazane_wz} konflikt={w.sklep_konflikt} /></td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatujDate(w.data_wystawienia)}</td>
              <td style={td}><ZnacznikWieku wiek_dni={w.wiek_dni} swieza={w.swieza} zalegla={w.zalegla} /></td>
              <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, color: Number(w.brutto_portal) < 0 ? 'var(--orange)' : 'var(--text)' }}>{formatujKwote(w.brutto_portal)}</td>
              <td style={td}><WSystemie wpisy={w.w_systemie} status={w.status} przezWz={w.przez_wz} numer={w.numer} /></td>
              <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{formatujDate(w.termin_platnosci)}</td>
              <td style={td}>
                <AkcjeWiersza wiersz={w} onDodaj={dodaj} onDodajOsoz={setOsozWiersz} onPobierzPlik={onPobierzPlik} onPdf={onPdf}
                  pobieranie={pobieranieId !== null && pobieranieId !== undefined && pobieranieId === w.id}
                  onPdfZPortalu={onPdfZPortalu}
                  pobieraniePdf={pobieraniePdfId !== null && pobieraniePdfId !== undefined && pobieraniePdfId === w.id} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!ladowanie && lista.length === 0 && (
        <div data-testid="pusta-lista" style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
          Brak dokumentów dla wybranego filtru
        </div>
      )}
      {ladowanie && (
        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>Ładowanie…</div>
      )}
    </div>
  );
};

export default TabelaKontroli;
